import { GetString } from 'fluent-react';
import React, { RefObject } from 'react';
import styled from 'styled-components';
import {
  Product,
  ProductClass,
  ProductLevel,
} from '../../graphQL/types/shared';
import {gql, useQuery} from '../../graphQL/useQuery';
import { generateStringProductId } from '../../graphQL/Utils';
import { SlideElementProps } from '../../presentationManager/PresentationManager';
import Selector from '../../sharedComponents/newCategorySelector/ProductCategorySelector';
import {
  categoryIconsContainerStyle,
} from '../../sharedComponents/newCategorySelector/sharedStyling';
import { HelpTooltipContainer } from '../SectorLabels';
import HelpTooltip from '../utilityBarItems/HelpTooltip';
import {
  SectorLabelsContainer as SectorLabelsContainerBase,
} from './../Grid';

const query = gql`
  query ProductCategorySelector($productClass: ProductClass!, $productLevel: ProductLevel!)
  {
    productCategorySelector: allProducts(productClass: $productClass, productLevel: $productLevel) {
      id
      productType
      shortName
    }
  }
`;

interface Variables {
  productClass: ProductClass;
  productLevel: ProductLevel | null;
}

interface SuccessResponse {
  productCategorySelector: Array<{
    id: Product['id']
    productType: Product['productType']
    shortName: Product['shortName'],
  }>;
}

const SectorLabelsContainer = styled(SectorLabelsContainerBase)`
  ${categoryIconsContainerStyle}
`;

interface Props extends SlideElementProps {
  zIndex: number;
  isServicesEnabled: boolean;
  isHelpActive: boolean;
  helpTooltipIndex: number;
  getFluentString: GetString;
}

const SectorLabels = React.forwardRef((props: Props, rootElRef: RefObject<HTMLDivElement>) => {
  const {
    zIndex, isServicesEnabled, isHelpActive, helpTooltipIndex, getFluentString,
  } = props;
  const productClass = ProductClass.HS;

  const variables: Variables = {
    productClass,
    productLevel: ProductLevel.section,
  };

  const rootStyle: React.CSSProperties = {
    zIndex,
  };

  const {loading, error, data} = useQuery<SuccessResponse, Variables>(query, {variables});
  let output: React.ReactElement<any> | null;
  if (loading === true) {
    output = null;
  } else if (error !== undefined) {
    output = null;
    console.error(error);
  } else if (data !== undefined) {
    const otherCategoryID = generateStringProductId({productClass: ProductClass.HS, id: 10});
    const allCategories = data.productCategorySelector.filter(category => category.id !== otherCategoryID);
    output = (
      <Selector
        allCategories={allCategories}
        allowSectorToggle={false}
        productClass={productClass}
        isServicesEnabled={isServicesEnabled}
        isServicesNotAvailableForAllYears={false}
        hasServicesNotAvailableForSomeYearsTooltipBeenShown={false}
        servicesDisabledMessage={{useAlternateMessage: false}}
      />
    );
  } else {
    output = null;
  }
  return (
    <SectorLabelsContainer
      style={rootStyle}
      ref={rootElRef}>
      <HelpTooltipContainer>
        <HelpTooltip
          index={helpTooltipIndex}
          term={getFluentString('help-tooltips-sector-icons-title')}
          explanation={getFluentString('help-tooltips-sector-icons-description')}
          isHelpActive={isHelpActive}
          styles={{transform: 'translateX(-50%)'}}
        />
      </HelpTooltipContainer>
      {output}
    </SectorLabelsContainer>
  );

});

export default SectorLabels;
